import { motion } from "framer-motion";
import { FileText, Building, Vote, Users, Trash2, Bus, Clock, Phone, Mail, MapPin, ExternalLink } from "lucide-react";
import { ServiceCard } from "@/components/Cards";
import { SERVICES_DATA, CONTACT_INFO } from "@/lib/index";
import { IMAGES } from "@/assets/images";

const iconMap = {
  FileText,
  Building,
  Vote,
  Users,
  Trash2,
  Bus,
};

export default function ViePratique() {
  const practicalInfoSections = [
    {
      title: "Horaires de la Mairie",
      icon: Clock,
      items: [
        { label: CONTACT_INFO.hours.weekdays },
        { label: CONTACT_INFO.hours.saturday },
        { label: `Dimanche : ${CONTACT_INFO.hours.sunday}` },
      ],
    },
    {
      title: "Coordonnées",
      icon: MapPin,
      items: [
        { label: CONTACT_INFO.address },
        { label: `${CONTACT_INFO.postalCode} ${CONTACT_INFO.city}` },
        { label: CONTACT_INFO.phone, icon: Phone },
        { label: CONTACT_INFO.email, icon: Mail },
      ],
    },
  ];

  const wasteSections = [
    {
      title: "Collecte des ordures ménagères",
      description: "Tous les mardis et vendredis matin. Sortir les bacs la veille au soir.",
    },
    {
      title: "Tri sélectif",
      description: "Collecte des emballages recyclables tous les jeudis. Bacs jaunes fournis par la mairie.",
    },
    {
      title: "Déchetterie",
      description: "Ouverte du lundi au samedi de 9h à 12h et de 14h à 18h. Carte d'accès gratuite disponible en mairie.",
    },
    {
      title: "Broyage des végétaux",
      description: "Service gratuit sur inscription. Deux passages par an (printemps et automne).",
    },
  ];

  const transportLines = [
    {
      line: "Ligne 8",
      route: "Gréasque - Aix-en-Provence",
      frequency: "Toutes les 30 minutes en semaine",
    },
    {
      line: "Ligne 12",
      route: "Gréasque - Gardanne",
      frequency: "Toutes les heures",
    },
    {
      line: "Transport scolaire",
      route: "Desserte des établissements scolaires",
      frequency: "Matin et soir en période scolaire",
    },
  ];

  return (
    <div className="min-h-screen bg-background">
      <div className="relative h-[40vh] min-h-[300px] overflow-hidden">
        <img
          src={IMAGES.MAIRIE_1}
          alt="Mairie de Gréasque"
          className="absolute inset-0 w-full h-full object-cover opacity-30"
        />
        <div className="absolute inset-0 bg-gradient-to-b from-background/50 via-transparent to-background/70" />
        <div className="relative h-full flex items-center justify-center">
          <motion.div
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ duration: 0.6 }}
            className="text-center px-4"
          >
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-foreground mb-4">
              Vie Pratique
            </h1>
            <p className="text-lg md:text-xl text-muted-foreground max-w-2xl mx-auto">
              Toutes les informations et services pour faciliter votre quotidien
            </p>
          </motion.div>
        </div>
      </div>

      <div className="container mx-auto px-4 py-16">
        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <h2 className="text-3xl md:text-4xl font-bold text-foreground mb-4 text-center">
            Services Municipaux
          </h2>
          <p className="text-muted-foreground text-center mb-12 max-w-2xl mx-auto">
            Accédez rapidement aux principaux services de la mairie
          </p>
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {SERVICES_DATA.map((service, index) => {
              const IconComponent = iconMap[service.icon as keyof typeof iconMap];
              return (
                <motion.div
                  key={service.id}
                  initial={{ opacity: 0, y: 24 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                >
                  <div className="bg-card rounded-2xl p-6 border border-border hover:shadow-lg transition-all duration-300 h-full">
                    <div className="flex items-start gap-4">
                      <div className="p-3 bg-primary/10 rounded-xl">
                        {IconComponent && <IconComponent className="w-6 h-6 text-primary" />}
                      </div>
                      <div className="flex-1">
                        <h3 className="text-xl font-semibold text-foreground mb-2">
                          {service.title}
                        </h3>
                        <p className="text-muted-foreground">{service.description}</p>
                      </div>
                    </div>
                  </div>
                </motion.div>
              );
            })}
          </div>
        </motion.section>

        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-20">
          {practicalInfoSections.map((section, index) => {
            const IconComponent = section.icon;
            return (
              <motion.div
                key={section.title}
                initial={{ opacity: 0, x: index === 0 ? -24 : 24 }}
                whileInView={{ opacity: 1, x: 0 }}
                viewport={{ once: true }}
                transition={{ duration: 0.6 }}
                className="bg-card rounded-2xl p-8 border border-border"
              >
                <div className="flex items-center gap-3 mb-6">
                  <div className="p-3 bg-primary/10 rounded-xl">
                    <IconComponent className="w-6 h-6 text-primary" />
                  </div>
                  <h3 className="text-2xl font-bold text-foreground">{section.title}</h3>
                </div>
                <div className="space-y-3">
                  {section.items.map((item, idx) => {
                    const ItemIcon = item.icon;
                    return (
                      <div key={idx} className="flex items-start gap-3">
                        {ItemIcon && <ItemIcon className="w-5 h-5 text-muted-foreground mt-0.5" />}
                        <p className="text-foreground">{item.label}</p>
                      </div>
                    );
                  })}
                </div>
              </motion.div>
            );
          })}
        </div>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mb-20"
        >
          <div className="bg-card rounded-2xl p-8 border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-accent/10 rounded-xl">
                <Trash2 className="w-6 h-6 text-accent" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">Gestion des Déchets</h2>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              {wasteSections.map((section, index) => (
                <motion.div
                  key={section.title}
                  initial={{ opacity: 0, y: 16 }}
                  whileInView={{ opacity: 1, y: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-muted/30 rounded-xl p-6"
                >
                  <h3 className="text-lg font-semibold text-foreground mb-2">
                    {section.title}
                  </h3>
                  <p className="text-muted-foreground">{section.description}</p>
                </motion.div>
              ))}
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <div className="bg-card rounded-2xl p-8 border border-border">
            <div className="flex items-center gap-3 mb-6">
              <div className="p-3 bg-primary/10 rounded-xl">
                <Bus className="w-6 h-6 text-primary" />
              </div>
              <h2 className="text-3xl font-bold text-foreground">Transports en Commun</h2>
            </div>
            <div className="space-y-4">
              {transportLines.map((transport, index) => (
                <motion.div
                  key={transport.line}
                  initial={{ opacity: 0, x: -16 }}
                  whileInView={{ opacity: 1, x: 0 }}
                  viewport={{ once: true }}
                  transition={{ duration: 0.4, delay: index * 0.1 }}
                  className="bg-muted/30 rounded-xl p-6 flex flex-col md:flex-row md:items-center md:justify-between gap-4"
                >
                  <div>
                    <h3 className="text-lg font-semibold text-foreground mb-1">
                      {transport.line}
                    </h3>
                    <p className="text-muted-foreground">{transport.route}</p>
                  </div>
                  <div className="text-sm text-muted-foreground md:text-right">
                    {transport.frequency}
                  </div>
                </motion.div>
              ))}
            </div>
            <div className="mt-6 pt-6 border-t border-border">
              <p className="text-muted-foreground text-sm">
                Pour plus d'informations sur les horaires et les tarifs, consultez le site de la{" "}
                <a
                  href="https://www.lepilote.com"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="text-primary hover:underline inline-flex items-center gap-1"
                >
                  Métropole Aix-Marseille-Provence
                  <ExternalLink className="w-4 h-4" />
                </a>
              </p>
            </div>
          </div>
        </motion.section>

        <motion.section
          initial={{ opacity: 0, y: 24 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
          className="mt-20"
        >
          <div className="bg-gradient-to-br from-primary/10 to-accent/10 rounded-2xl p-8 text-center">
            <h2 className="text-2xl md:text-3xl font-bold text-foreground mb-4">
              Besoin d'aide ?
            </h2>
            <p className="text-muted-foreground mb-6 max-w-2xl mx-auto">
              Notre équipe est à votre disposition pour répondre à toutes vos questions et vous accompagner dans vos démarches.
            </p>
            <a
              href="#/contact"
              className="inline-flex items-center gap-2 bg-primary text-primary-foreground px-6 py-3 rounded-xl font-semibold hover:bg-primary/90 transition-colors"
            >
              Nous contacter
              <ExternalLink className="w-5 h-5" />
            </a>
          </div>
        </motion.section>
      </div>
    </div>
  );
}